import store from '/@/store'
import { getRoot } from '/@/root'
import { ELocalStorageKey, EDeviceTypeName } from '/@/types'
import { getDeviceBySn } from '/@/api/manage'
import { message } from 'ant-design-vue'
import dockIcon from '/@/assets/icons/dock.png'
import rcIcon from '/@/assets/icons/rc.png'
import droneIcon from '/@/assets/icons/drone.png'
import { gcj02towgs84, wgs84togcj02 } from '/@/vendors/coordtransform'
import { OSDVisible } from '/@/types/device'
import type { RootStateType } from '/@/store'
import { getOsdMapFocusTarget, getOsdMapTargets } from '/@/utils/osdMapFocus'

export function deviceTsaUpdate () {
  const root = getRoot()
  let AMap = root.$aMap

  const icons = new Map([
    [EDeviceTypeName.Aircraft, droneIcon],
    [EDeviceTypeName.Gateway, rcIcon],
    [EDeviceTypeName.Dock, dockIcon]
  ])
  const markers = store.state.markerInfo.coverMap
  const paths = store.state.markerInfo.pathMap

  let trackLine = null as any
  function getTrackLineInstance () {
    if (!trackLine) {
      trackLine = new AMap.Polyline({
        map: root.$map,
        strokeColor: '#939393' // 线颜色
      })
    }
    return trackLine
  }

  function initIcon (type: number) {
    return new AMap.Icon({
      image: icons.get(type),
      imageSize: new AMap.Size(40, 40),
      size: new AMap.Size(40, 40)
    })
  }

  function formatCoord (value: number): string {
    return value.toFixed(6)
  }

  function resolveLabelWgs (
    wgsLongitude?: number,
    wgsLatitude?: number,
    gcjLongitude?: number,
    gcjLatitude?: number,
  ): [number, number] | null {
    if (
      wgsLongitude != null && wgsLatitude != null
      && !Number.isNaN(wgsLongitude) && !Number.isNaN(wgsLatitude)
      && !(wgsLongitude === 0 && wgsLatitude === 0)
    ) {
      return [wgsLongitude, wgsLatitude]
    }
    if (gcjLongitude != null && gcjLatitude != null) {
      const [lng, lat] = gcj02towgs84(gcjLongitude, gcjLatitude)
      if (!(lng === 0 && lat === 0)) {
        return [lng, lat]
      }
    }
    return null
  }

  function buildMarkerLabel (
    sn: string,
    wgsLongitude?: number,
    wgsLatitude?: number,
    gcjLongitude?: number,
    gcjLatitude?: number,
  ) {
    const wgs = resolveLabelWgs(wgsLongitude, wgsLatitude, gcjLongitude, gcjLatitude)
    const coordLine = wgs
      ? `<div style="opacity:0.9;font-size:10px;margin-top:2px;">经 ${formatCoord(wgs[0])}° 纬 ${formatCoord(wgs[1])}°</div>`
      : ''
    const label: Record<string, unknown> = {
      content: `<div style="background:rgba(0,0,0,0.82);color:#fff;font-size:11px;line-height:1.35;padding:3px 8px;border-radius:4px;border:1px solid rgba(0,238,139,0.55);"><div style="font-weight:600;">${sn}</div>${coordLine}</div>`,
      direction: 'top',
    }
    if (AMap?.Pixel) {
      label.offset = new AMap.Pixel(0, wgs ? -56 : -44)
    }
    return label
  }

  function buildMarkerTitle (name: string, sn: string, wgs?: [number, number] | null) {
    const base = name ? `${name} (${sn})` : sn
    if (!wgs) return base
    return `${base}\n经 ${formatCoord(wgs[0])}° 纬 ${formatCoord(wgs[1])}°`
  }

  function applyMarkerLabel (
    marker: any,
    sn: string,
    wgsLongitude?: number,
    wgsLatitude?: number,
    gcjLongitude?: number,
    gcjLatitude?: number,
  ) {
    marker.setLabel(buildMarkerLabel(sn, wgsLongitude, wgsLatitude, gcjLongitude, gcjLatitude))
  }

  function initMarker (
    type: number,
    name: string,
    sn: string,
    lng?: number,
    lat?: number,
    wgsLongitude?: number,
    wgsLatitude?: number,
  ) {
    if (root.$aMap === undefined) {
      return
    }
    AMap = root.$aMap
    const label = buildMarkerLabel(sn, wgsLongitude, wgsLatitude, lng, lat)
    const wgs = resolveLabelWgs(wgsLongitude, wgsLatitude, lng, lat)
    if (markers[sn]) {
      markers[sn].setLabel(label)
      markers[sn].setTitle(buildMarkerTitle(name, sn, wgs))
      if (lng != null && lat != null) {
        markers[sn].setPosition(new AMap.LngLat(lng, lat))
      }
      return
    }
    markers[sn] = new AMap.Marker({
      position: new AMap.LngLat(lng || 113.943225499, lat || 22.577673716),
      icon: initIcon(type),
      title: buildMarkerTitle(name, sn, wgs),
      anchor: 'top-center',
      offset: [0, -20],
      label,
    })
    root.$map.add(markers[sn])
  }

  function removeMarker (sn: string) {
    if (!markers[sn]) {
      return
    }
    root.$map.remove(markers[sn])
    getTrackLineInstance().setPath([])
    delete markers[sn]
    delete paths[sn]
  }

  function addMarker (sn: string, lng?: number, lat?: number) {
    getDeviceBySn(localStorage.getItem(ELocalStorageKey.WorkspaceId)!, sn)
      .then(data => {
        if (data.code !== 0) {
          message.error(data.message)
          return
        }
        initMarker(data.data.domain, data.data.nickname, sn, lng, lat)
      })
  }

  function moveTo (
    sn: string,
    lng: number,
    lat: number,
    immediate = false,
    wgsLongitude?: number,
    wgsLatitude?: number,
  ) {
    let marker = markers[sn]
    if (!marker) {
      addMarker(sn, lng, lat)
      return
    }
    applyMarkerLabel(marker, sn, wgsLongitude, wgsLatitude, lng, lat)
    if (immediate) {
      marker.setPosition([lng, lat])
      return
    }
    marker.moveTo([lng, lat], {
      duration: 1800,
      autoRotation: true
    })
  }

  function focusOsdSelection (
    osd: OSDVisible,
    deviceState: RootStateType['deviceState'],
    options?: { centerMap?: boolean },
  ) {
    if (!osd.visible || !root.$map) {
      return
    }

    const centerMap = options?.centerMap !== false
    const targets = getOsdMapTargets(osd, deviceState)
    const focusTarget = getOsdMapFocusTarget(targets, { preferDock: osd.is_dock })

    if (targets.length === 0) {
      const sn = osd.is_dock ? osd.gateway_sn : osd.sn
      if (sn) {
        const type = osd.is_dock ? EDeviceTypeName.Dock : EDeviceTypeName.Aircraft
        const name = osd.is_dock ? (osd.gateway_callsign || sn) : (osd.callsign || sn)
        initMarker(type, name, sn)
      }
      return
    }

    targets.forEach(target => {
      const [lng, lat] = wgs84togcj02(target.longitude, target.latitude)
      if (markers[target.sn]) {
        moveTo(target.sn, lng, lat, !centerMap, target.longitude, target.latitude)
      } else {
        initMarker(target.type, target.name, target.sn, lng, lat, target.longitude, target.latitude)
      }
    })

    if (centerMap && focusTarget) {
      const [lng, lat] = wgs84togcj02(focusTarget.longitude, focusTarget.latitude)
      root.$map.setZoomAndCenter(Math.max(root.$map.getZoom() || 0, 16), [lng, lat])
    }
  }

  return {
    marker: markers,
    initMarker,
    removeMarker,
    moveTo,
    focusOsdSelection,
  }
}
